import React, { useCallback, useMemo } from "react";

import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { Drawer } from "components/Measure-data/Drawer/Drawer";
import { GroupsForm } from "components/Measure-data/Groups-form/GroupsForm";
import {
  Group,
  MeasureData,
  MeasureDataGroup,
  MeasureDataRound
} from "components/Measure-data/Groups-form/GroupsFormContext";
import { DEBT_MEASURE_FETCHABLE_NAME, DEBT_MEASURE_ROLES } from "constants/debt";
import { useActionHook } from "hooks";
import { useDealActions } from "store/actions-context";
import type { IMeasureActions } from "store/actions/deal-actions/measure-actions";
import {
  useDealDefaultCurrencySelect,
  useDealIdSelector,
  useDealSubAssetsSelector,
  useParentDealSelect
} from "store/selectors/deal-selectors/deal-selectors";
import { useCreateManyMeasureDataSelect } from "store/selectors/view-selectors";
import { MeasureDataDraft } from "store/types";
import { AmortizationRow, DebtSegment } from "store/types/deal/debt";

import { useMeasureDrawer } from "../../hooks/debt-hooks/use-measure-drawer";

interface AddValueDrawerProps {
  open: boolean;
  onClose: () => void;
  rows: AmortizationRow[] | undefined;
  segments: DebtSegment[];
}

const getRowSegment = (row: AmortizationRow, segments: DebtSegment[]) => {
  return segments.find((segment) => row.segmentIds.includes(segment.id));
};

const Content: React.FC<{
  onClose: () => void;
  rows: AmortizationRow[];
  segments: DebtSegment[];
  action: IMeasureActions["createManyMeasureDataAction"];
  loading: boolean;
  saveMeasureDataDraftAction: IMeasureActions["saveMeasureDataDraftAction"];
}> = ({ onClose, rows, segments, action, loading, saveMeasureDataDraftAction }) => {
  const dealIds = useMemo(
    () => rows.map((row) => getRowSegment(row, segments)?.deal_id).filter(Boolean) as number[],
    [rows, segments]
  );
  const { addMeasureToForm, rounds, roundsLoading, getMeasuresOfFetchable } = useMeasureDrawer(dealIds);
  const debtMeasures = getMeasuresOfFetchable(DEBT_MEASURE_FETCHABLE_NAME);
  const currency = useDealDefaultCurrencySelect() || "USD";
  const dealId = useDealIdSelector()!;
  const subDeals = useDealSubAssetsSelector();
  const parent = useParentDealSelect();
  const createMeasureData = useCreateManyMeasureDataSelect();

  const principalMeasure = debtMeasures.find((param) => param.roles[0] === DEBT_MEASURE_ROLES.principal);
  const interestPaymentMeasure = debtMeasures.find((param) => param.roles[0] === DEBT_MEASURE_ROLES.interest);
  const feesPaymentMeasure = debtMeasures.find((param) => param.roles[0] === DEBT_MEASURE_ROLES.creditFacilityFee);

  const postMeasureData = useCallback(
    async (data: { groups: MeasureDataGroup[]; roundsByDeals: Record<number, MeasureDataRound[]> }) => {
      await action(data);
      onClose();
    },
    [action, onClose]
  );

  const handleSaveAsDraft = useCallback(
    async (data: MeasureDataDraft["data"]) => {
      if (!createMeasureData?.dealId) return;

      await saveMeasureDataDraftAction({
        deal_id: createMeasureData.dealId,
        data,
        ...(createMeasureData.draftId && { id: createMeasureData.draftId })
      });
      onClose();
    },
    [saveMeasureDataDraftAction, createMeasureData, onClose]
  );

  const initialData = useMemo(() => {
    const measureData: MeasureData[] = [];
    const groups: Group[] = [];
    const measures = [principalMeasure, interestPaymentMeasure, feesPaymentMeasure];

    if (rounds) {
      rows.forEach((row) => {
        const segment = getRowSegment(row, segments);
        if (segment && rounds[segment.deal_id]?.length) {
          const addMeasure = addMeasureToForm(rounds[segment.deal_id][0], measureData, groups);
          const values = [row.principal_payment, row.interest_payment, row.fees_payment];
          values.forEach((value, index) => {
            if (value && measures[index]) {
              addMeasure({ value, date: row.date, subDealId: segment.deal_id, measureId: measures[index]!.id });
            }
          });
        }
      });
    }
    return { measureData, groups };
  }, [rows, segments, rounds, addMeasureToForm, principalMeasure, interestPaymentMeasure, feesPaymentMeasure]);

  return (
    <AsyncContentMui isLoading={loading || roundsLoading}>
      {rounds && (
        <GroupsForm
          deal_id={dealId}
          subDeals={subDeals}
          parentId={parent?.id || null}
          initialRoundsByDeal={rounds}
          initialGroups={initialData.groups}
          initialMeasureData={initialData.measureData}
          onSave={postMeasureData}
          onSaveAsDraft={handleSaveAsDraft}
          onClose={onClose}
          disableDealSelect
          disableSubDealSelect
          defaultCurrency={currency}
        />
      )}
    </AsyncContentMui>
  );
};

export const AddValueDrawer: React.FC<AddValueDrawerProps> = ({ open, onClose, rows, segments }) => {
  const { createManyMeasureDataAction, saveMeasureDataDraftAction } = useDealActions();
  const { action, loading } = useActionHook(createManyMeasureDataAction);

  return (
    <Drawer anchor="left" open={open} handleClose={onClose} title="Add Prepayment">
      {rows && segments.length > 0 && (
        <Content
          onClose={onClose}
          rows={rows}
          segments={segments}
          action={action}
          loading={loading}
          saveMeasureDataDraftAction={saveMeasureDataDraftAction}
        />
      )}
    </Drawer>
  );
};
